import React, { useContext, useEffect, useState } from 'react'
import Link from 'gatsby-link'
import styled, { css } from 'styled-components'
import { Variables } from '../styles/Variables'
import LayoutContext from './LayoutContext'
import media from 'css-in-js-media'

import ArrowRight from '../svg/arrow-right.svg'
import ArrowLeft from '../svg/arrow-left.svg'
import CheckIcon from '../svg/check.svg'
import { motion, useAnimation } from 'framer-motion'

export const LanguageLinks = styled.ul`
    list-style: none;
    margin: 0;
    padding: 0;
    position: absolute;
    top: 100%;
    left: 50%;
    padding: 7.5px 20px;
    display: flex;
    background-color: white;
    margin-top: 30px;
    white-space: nowrap;
    flex-direction: column;
    min-width: 176px;
    box-sizing: border-box;
    pointer-events: none;
    opacity: 0;
    transition: 0.3s opacity ease-out, 0.3s transform ease-out;
    transition-timing-function: cubic-bezier(0.29, 0.46, 0.1, 1);
    transform: translateX(-50%) translateY(-10px);

    will-change: opacity, transform;

    ${media('<=desktop')} {
        margin-top: 12px;
        transform: none;
        left: 0;
    }

    svg {
        height: 14px;
        width: 14px;
    }

    a {
        display: flex;
        align-items: center;
        justify-content: space-between;

        ${media('<desktop')} {
            color: ${Variables.color.pazola};
        }
    }
`
const Arrow = styled.div`
    display: flex;
    align-items: center;
    margin-left: 7px;
    svg {
        transition: 0.2s transform ease-out;
    }
`

export const LanguageSwitcher = styled.div`
    position: relative;
    margin-right: 22px;
    padding: 15px;
    color: ${Variables.color.white};
    cursor: pointer;
    text-transform: uppercase;
    font-family: ${Variables.font.bodyBold};
    font-size: 14px;
    line-height: 17px;
    text-align: center;

    ${media('>=desktop')} {
        padding-left: 0;
    }

    ${media('<desktop')} {
        font-size: 12px;
        line-height: 14px;
    }

    :hover {
        color: ${Variables.color.pazola};

        ${Arrow} svg {
            transform: rotate(-180deg);
        }

        ${LanguageLinks} {
            transform: translateX(-50%) translateY(0);
            opacity: 1;
            pointer-events: all;
            :before {
                content: '';
                bottom: 100%;
                left: 10px;
                right: 35px;
                height: 80px;
                position: absolute;
            }

            ${media('<desktop')} {
                transform: none;
            }
        }
    }
`

const LanguageLinkWrap = styled.li`
    margin: 7.5px 0;
`

const LanguageLink = styled(Link)`
    font-family: ${Variables.font.body};
    color: ${Variables.color.white};
    text-decoration: none;

    :hover {
        opacity: 0.8;
    }
`

export const InnerWrapper = styled.div`
    display: flex;
    align-items: center;
    text-transform: uppercase;
    svg path {
        stroke: ${Variables.color.white};
    }
    ${media('<desktop')} {
        ${Arrow} {
            stroke: ${Variables.color.white};
        }
    }
`

export const LanguageSelected = styled.div`
    line-height: 120%;
    font-family: ${Variables.font.bodyBold};
    color: ${Variables.color.white};
`

const DesktopView = styled.div`
    display: none;
    ${media('>=desktop')} {
        display: block;
    }
`

const MobileView = styled.div`
    margin-bottom: 20px;

    svg path {
        stroke: ${Variables.color.white};
    }

    ${media('>=desktop')} {
        display: none;
    }
`
const MobileTrigger = styled.div`
    display: flex;
    text-transform: uppercase;
    font-weight: 500;
    font-size: 16px;
    line-height: 120%;
    cursor: pointer;

    svg {
        height: 20px;
        width: 20px;
        margin-left: 20px;

        path {
            stroke: ${Variables.color.white};
        }
    }
`

const MobileContent = styled(motion.div)`
    position: fixed;
    width: 100%;
    bottom: 0;
    top: 0;
    left: 0;
    pointer-events: none;
    z-index: 10;
    background-color: ${Variables.color.cocoa};
    opacity: 0;
    padding: 40px 30px 40px;
    box-sizing: border-box;

    ${(props) =>
        props.isActive &&
        css`
            pointer-events: all;
        `}
`

const CloseTrigger = styled.div`
    cursor: pointer;

    svg path {
        stroke: ${Variables.color.white};
    }
`

const MobileLinks = styled.ul`
    list-style: none;
    margin: 80px 0 0;
    padding: 0;

    svg {
        width: 20px;
        height: 20px;

        path {
            stroke: ${Variables.color.white};
        }
    }

    li {
        margin-bottom: 30px;
    }

    a {
        color: ${Variables.color.white};
        font-weight: normal;
        font-size: 16px;
        line-height: 120%;
        letter-spacing: 0.0015em;
        display: flex;
        justify-content: space-between;
        align-items: center;
        text-transform: uppercase;
    }
`

const setLanguageDetected = () => {
    localStorage.setItem('PozalaLanguageDetected', 'yes')
}

const SelectLanguage = () => {
    const { languageData, currentLang, isLanguageMenuOpen, setLanguageMenuOpen } = useContext(LayoutContext)

    const controls = useAnimation()

    useEffect(() => {
        controls.start(isLanguageMenuOpen ? 'active' : 'inactive') // Second should be 'inactive'
    }, [isLanguageMenuOpen, controls])

    const LanguageMenuStyles = {
        active: {
            opacity: 1,
        },
        inactive: {
            opacity: 0,
        },
    }

    const langSelected = currentLang.langKeyShort
    const links = languageData.map((lang) => (
        <LanguageLinkWrap key={lang.langKey}>
            <LanguageLink to={lang.link} key={lang.langKey} onClick={() => setLanguageDetected()}>
                {lang.labelDesktop}
                {lang.selected && <CheckIcon />}
            </LanguageLink>
        </LanguageLinkWrap>
    ))

    const linksMobile = languageData.map((lang) => (
        <LanguageLinkWrap key={lang.langKey}>
            <LanguageLink to={lang.link} key={lang.langKey} onClick={() => setLanguageDetected()}>
                {lang.labelFull}
                {lang.selected && <CheckIcon />}
            </LanguageLink>
        </LanguageLinkWrap>
    ))

    return (
        <>
            {/* <DesktopView> */}
            <LanguageSwitcher>
                <InnerWrapper>
                    <LanguageSelected>{langSelected}</LanguageSelected>
                    <Arrow>
                        <svg width="17" height="10" viewBox="0 0 17 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path
                                d="M15.7324 0.767578L8.23242 8.18261L0.732422 0.767577"
                                stroke="white"
                                strokeWidth="2"
                                strokeMiterlimit="10"
                            />
                        </svg>
                    </Arrow>
                </InnerWrapper>
                <LanguageLinks>{links}</LanguageLinks>
            </LanguageSwitcher>
            {/* </DesktopView> */}
            {/* <MobileView className="navigation-open">
                <MobileTrigger
                    onClick={() => {
                        setLanguageMenuOpen(true)
                    }}
                >
                    <LanguageSelected>{langSelected}</LanguageSelected>
                    <ArrowRight />
                </MobileTrigger>
                <MobileContent
                    animate={controls}
                    variants={LanguageMenuStyles}
                    transition={{ type: 'tween' }}
                    isActive={isLanguageMenuOpen}
                >
                    <CloseTrigger
                        onClick={() => {
                            setLanguageMenuOpen(false)
                        }}
                    >
                        <ArrowLeft />
                    </CloseTrigger>
                    <MobileLinks>{linksMobile}</MobileLinks>
                </MobileContent>
            </MobileView> */}
        </>
    )
}

export default SelectLanguage
